import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@core/services/api/apiRequest";
import type { TalkJSData } from "../chat.types";

export type UseFetchTalkJSDataArgs = {
  /** Current user (to determine if logged in) */
  user: { id: number } | null;
  /** Whether the query is enabled */
  isEnabled?: boolean;
};

/**
 * Fetches TalkJS configuration data from the API.
 * React Query is the source of truth.
 *
 * @example
 * ```typescript
 * const user = useStore((s) => s.user);
 * const { data: talkjsData, isLoading } = useFetchTalkJSData({ user });
 *
 * if (isLoading) return <Loader />;
 *
 * return <ChatButton talkjsData={talkjsData} />;
 * ```
 */
export function useFetchTalkJSData({
  user,
  isEnabled = true,
}: UseFetchTalkJSDataArgs) {
  return useQuery<TalkJSData | null, Error>({
    queryKey: ["talkjs", user?.id],
    queryFn: async () => {
      const response = await apiRequest("/api/get-talkjs-data/", "GET");
      return response ?? null;
    },
    enabled: !!user && isEnabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export default useFetchTalkJSData;

